// src/pages/ChangeAvatar.tsx
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { XMarkIcon } from "@heroicons/react/24/solid";
import { Button } from "../components/Button";
import { A } from "../styles/appleClasses";

const avatarStyles = ["adventurer", "bottts", "micah", "notionists", "pixel-art"];
const avatarSeeds = ["Ricebook", "Wayne", "Admin", "Cat", "Smile", "Happy", "Cool", "Star"];

type Props = {
  mode?: "page" | "inlay";
  initialStyle?: string;
  initialSeed?: string;
  onClose?: () => void;
  onSave?: (style: string, seed: string) => void;
};

export default function ChangeAvatar({
  mode = "inlay",
  initialStyle = "adventurer",
  initialSeed = "Ricebook",
  onClose,
  onSave,
}: Props) {
  const { t } = useTranslation();
  // Keep the handler as a code element while allowing either locale's word order.
  const [missingBefore, missingAfter] = t("changeAvatar.missingSaveHandler", {
    handler: "\0",
    caller: "AccountMenu",
  }).split("\0");
  const [style, setStyle] = useState(initialStyle);
  const [seed, setSeed] = useState(initialSeed);

  const avatarUrl = useMemo(() => {
    return `https://api.dicebear.com/7.x/${style}/svg?seed=${encodeURIComponent(seed)}`;
  }, [style, seed]);

  const handleClose = () => {
    if (mode === "inlay") onClose?.();
  };

  const handleSave = () => {
    onSave?.(style, seed);
    // match the other inlays: close after save
    setTimeout(() => onClose?.(), 150);
  };

  const content = (
    <>
      <div className="flex items-center gap-3 mb-4">
        <img
          src={avatarUrl}
          alt={t("changeAvatar.preview")}
          className="w-14 h-14 rounded-full border border-gray-200 object-cover"
        />
        <div className="min-w-0">
          <div className={A.title}>{t("changeAvatar.preview")}</div>
          <div className={A.label}>{t("changeAvatar.chooseHint")}</div>
        </div>
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white p-3">
        <div className="flex flex-wrap gap-2 max-h-56 overflow-y-auto justify-center">
          {avatarStyles.map((s) =>
            avatarSeeds.map((z) => {
              const selected = s === style && z === seed;
              const url = `https://api.dicebear.com/7.x/${s}/svg?seed=${encodeURIComponent(z)}`;

              return (
                <button
                  key={s + z}
                  type="button"
                  onClick={() => {
                    setStyle(s);
                    setSeed(z);
                  }}
                  className={[
                    "rounded-full",
                    "focus:outline-none focus:ring-2 focus:ring-appleblueD",
                  ].join(" ")}
                  aria-label={t("changeAvatar.selectAvatar", { style: s, seed: z })}
                >
                  <img
                    src={url}
                    alt={z}
                    className={[
                      "w-12 h-12 rounded-full border",
                      selected
                        ? "border-appleblue ring-2 ring-appleblueD"
                        : "border-gray-200 hover:border-gray-300",
                    ].join(" ")}
                  />
                </button>
              );
            })
          )}
        </div>
      </div>

      <div className="mt-4 flex gap-3">
        <Button
          type="button"
          variant="secondary"
          className="flex-1 justify-center"
          onClick={handleClose}
        >
          {t("directory.common.cancel")}
        </Button>
        <Button
          type="button"
          variant="primary"
          className="flex-1 justify-center"
          onClick={handleSave}
        >
          {t("directory.common.save")}
        </Button>
      </div>

      {!onSave && (
        <div className="mt-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700">
          {missingBefore}<code>onSave</code>{missingAfter}
        </div>
      )}
    </>
  );

  if (mode === "inlay") {
    return (
      <div
        className="fixed inset-0 z-[1000] bg-black/40 overflow-y-auto"
        role="dialog"
        aria-modal="true"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) onClose?.();
        }}
      >
        {/* top-align so content feels consistent with other modals */}
        <div className="min-h-full flex items-start justify-center p-4 pt-16 pb-10">
          {/* keep overflow-visible so nothing clips */}
          <div className={`w-full max-w-lg ${A.modal} overflow-visible`}>
            {/* centered title header */}
            <div className="grid grid-cols-[40px_1fr_40px] items-center px-5 py-4 border-b border-gray-100">
              <div />
              <h3 className={`${A.title} text-center`}>{t("accountMenu.changeAvatar")}</h3>
              <button
                onClick={handleClose}
                className={[
                  A.iconBtn,
                  "justify-self-end inline-flex items-center justify-center",
                  "hover:bg-gray-100",
                  "focus:outline-none focus:ring-2 focus:ring-appleblueD",
                ].join(" ")}
                aria-label={t("directory.common.close")}
                type="button"
              >
                <XMarkIcon className="w-5 h-5 text-gray-400 hover:text-gray-600 transition" />
              </button>
            </div>

            <div className={`px-6 py-6 ${A.body}`}>{content}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gray-50 ${A.body} flex justify-center px-2 py-6`}>
      <div className={`w-full max-w-md ${A.card} p-6`}>
        <div className="text-center mb-4">
          <h2 className={A.title}>{t("accountMenu.changeAvatar")}</h2>
        </div>
        {content}
      </div>
    </div>
  );
}
